<template>
  <div class="overview">
    <div v-if="loading" class="summary-grid skeleton-summary" role="status" aria-live="polite">
      <span class="sr-only">Cargando balance...</span>
      <section class="card highlight">
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line lg" style="width: 42%"></div>
          <div class="skeleton skeleton-line xl" style="width: 72%"></div>
          <div class="skeleton skeleton-line" style="width: 48%"></div>
        </div>
        <div class="skeleton-kpis">
          <div class="skeleton skeleton-line" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 55%"></div>
          <div class="skeleton skeleton-line" style="width: 62%"></div>
          <div class="skeleton skeleton-line" style="width: 46%"></div>
        </div>
      </section>
      <section class="card chart skeleton-chart">
        <div class="skeleton skeleton-circle" style="width: 140px; height: 140px"></div>
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line lg" style="width: 48%"></div>
          <div class="skeleton skeleton-line" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 60%"></div>
          <div class="skeleton skeleton-line" style="width: 54%"></div>
        </div>
      </section>
      <section class="card chart skeleton-chart">
        <div class="skeleton skeleton-circle" style="width: 140px; height: 140px"></div>
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line lg" style="width: 50%"></div>
          <div class="skeleton skeleton-line" style="width: 72%"></div>
          <div class="skeleton skeleton-line" style="width: 58%"></div>
          <div class="skeleton skeleton-line" style="width: 62%"></div>
        </div>
      </section>
    </div>

    <section v-if="loading" class="card chart skeleton-bars" aria-hidden="true">
      <div class="skeleton skeleton-line lg" style="width: 32%"></div>
      <div class="skeleton-stack">
        <div class="skeleton skeleton-line" style="width: 95%"></div>
        <div class="skeleton skeleton-line" style="width: 90%"></div>
        <div class="skeleton skeleton-line" style="width: 88%"></div>
        <div class="skeleton skeleton-line" style="width: 92%"></div>
      </div>
    </section>

    <section v-if="loading" class="card table-card skeleton-table" aria-hidden="true">
      <div class="skeleton skeleton-line lg" style="width: 28%"></div>
      <div class="skeleton-table-rows">
        <div class="skeleton skeleton-line" style="width: 100%"></div>
        <div class="skeleton skeleton-line" style="width: 96%"></div>
        <div class="skeleton skeleton-line" style="width: 98%"></div>
        <div class="skeleton skeleton-line" style="width: 94%"></div>
        <div class="skeleton skeleton-line" style="width: 97%"></div>
      </div>
    </section>

    <div class="summary-grid" v-if="viewSummary">
      <section class="card highlight">
        <div class="card-header">
          <h2>{{ summaryTitle }}</h2>
          <span class="chip">Actualizado</span>
        </div>
        <div class="totals">
          <div class="total-hero">
            <h3 :class="valueClass(viewSummary.totals)">
              {{ formatEur(viewSummary.totals.currentValueEur) }}
            </h3>
          </div>
          <div>
            <p>Neto invertido</p>
            <h3>{{ formatEur(viewSummary.totals.totalPaidEur) }}</h3>
          </div>
          <div>
            <p>Valor actual</p>
            <h3>{{ formatEur(viewSummary.totals.currentValueEur) }}</h3>
          </div>
          <div>
            <p>Comisiones</p>
            <h3>{{ formatEur(viewSummary.totals.totalFeesEur) }}</h3>
          </div>
          <div>
            <p>Beneficio/Pérdida</p>
            <h3 :class="profitClass(viewSummary.totals.profitLossPct)">
              {{ formatPct(viewSummary.totals.profitLossPct) }}
            </h3>
          </div>
        </div>
      </section>

      <section class="card chart">
        <h2>Distribución de la cartera</h2>
        <div class="donut-wrap">
          <div class="donut">
            <svg class="donut-svg" viewBox="0 0 120 120" aria-hidden="true">
              <circle class="donut-ring" cx="60" cy="60" r="42"></circle>
              <circle
                v-for="segment in donutSegments"
                :key="segment.key"
                class="donut-segment"
                cx="60"
                cy="60"
                r="42"
                :stroke="segment.color"
                :stroke-dasharray="segment.dashArray"
                :stroke-dashoffset="segment.dashOffset"
                @mousemove="onDonutMove($event, segment, 'group')"
                @mouseleave="hideDonutTooltip('group')"
              ></circle>
            </svg>
            <div
              v-if="donutTooltip.visible && donutTooltip.type === 'group'"
              class="donut-tooltip"
              :style="{ left: `${donutTooltip.x}px`, top: `${donutTooltip.y}px` }"
            >
              <strong>{{ donutTooltip.label }}</strong>
              <span>{{ formatShare(donutTooltip.pct) }}</span>
            </div>
          </div>
          <div class="donut-legend">
            <div v-for="(group, index) in viewSummary.groups" :key="group.group" class="legend-item">
              <span class="swatch" :style="{ background: palette[index % palette.length] }"></span>
              <div>
                <strong>{{ groupLabel(group.group) }}</strong>
                <p>{{ formatEur(group.currentValueEur) }}</p>
              </div>
              <span class="legend-pct">{{ formatShare(group.sharePct) }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="card chart">
        <h2>Distribución por plataforma</h2>
        <div class="donut-wrap">
          <div class="donut">
            <svg class="donut-svg" viewBox="0 0 120 120" aria-hidden="true">
              <circle class="donut-ring" cx="60" cy="60" r="42"></circle>
              <circle
                v-for="segment in platformSegments"
                :key="segment.key"
                class="donut-segment"
                cx="60"
                cy="60"
                r="42"
                :stroke="segment.color"
                :stroke-dasharray="segment.dashArray"
                :stroke-dashoffset="segment.dashOffset"
                @mousemove="onDonutMove($event, segment, 'platform')"
                @mouseleave="hideDonutTooltip('platform')"
              ></circle>
            </svg>
            <div
              v-if="donutTooltip.visible && donutTooltip.type === 'platform'"
              class="donut-tooltip"
              :style="{ left: `${donutTooltip.x}px`, top: `${donutTooltip.y}px` }"
            >
              <strong>{{ donutTooltip.label }}</strong>
              <span>{{ formatShare(donutTooltip.pct) }}</span>
            </div>
          </div>
          <div class="donut-legend">
            <div
              v-for="(platform, index) in platformGroups"
              :key="platform.platform"
              class="legend-item"
            >
              <span class="swatch" :style="{ background: palette[index % palette.length] }"></span>
              <div>
                <strong>{{ platformLabel(platform.platform) }}</strong>
                <p>{{ formatEur(platform.currentValueEur) }}</p>
              </div>
              <span class="legend-pct">{{ formatShare(platform.sharePct) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <section class="card chart" v-if="viewSummary">
      <div class="card-header">
        <h2>Top assets por valor</h2>
        <p class="muted">Balance actual en EUR</p>
      </div>
      <div class="bars">
        <div v-for="asset in topAssets" :key="asset.asset" class="bar-row">
          <div class="bar-meta">
            <span class="tag">{{ asset.asset }}</span>
            <small class="muted">{{ groupLabel(asset.group) }}</small>
          </div>
          <div class="bar-track">
            <div class="bar-fill" :style="{ width: barWidth(asset.currentValueEur, topMax) }"></div>
          </div>
          <div class="bar-value">{{ formatEur(asset.currentValueEur) }}</div>
        </div>
      </div>
    </section>

    <section class="card table-card" v-if="viewSummary">
      <div class="card-header">
        <div>
          <h2>Assets en cartera</h2>
          <p class="muted">Haz click para ver el detalle</p>
        </div>
        <div class="filters">
          <button
            class="filter-chip"
            :class="{ active: selectedType === 'ALL' }"
            @click="selectedType = 'ALL'"
          >
            Todos
          </button>
          <button
            v-for="type in availableTypes"
            :key="type"
            class="filter-chip"
            :class="{ active: selectedType === type }"
            @click="selectedType = type"
          >
            {{ groupLabel(type) }}
          </button>
        </div>
      </div>
      <div class="table">
        <div class="row head">
          <span>Asset</span>
          <span>Grupo</span>
          <span>Neto invertido</span>
          <span>Valor actual</span>
          <span>Resultado</span>
        </div>
        <button
          class="row"
          v-for="asset in filteredAssets"
          :key="asset.asset"
          @click="goToAsset(asset.asset)"
        >
          <span class="asset-name">
            <span class="tag">{{ asset.asset }}</span>
            <small>{{ groupLabel(asset.group) }}</small>
          </span>
          <span>{{ groupLabel(asset.group) }}</span>
          <span>{{ formatEur(asset.totalPaidEur) }}</span>
          <span>{{ asset.currentValueEur ? formatEur(asset.currentValueEur) : "Sin precio" }}</span>
          <span :class="profitClass(asset.profitLossPct)">{{ formatPct(asset.profitLossPct) }}</span>
        </button>
      </div>
      <div v-if="missingPriceAssetsFiltered.length" class="notice">
        Assets sin precio actual: {{ missingPriceAssetsFiltered.join(", ") }}
      </div>
    </section>

    <section v-if="error" class="state error">{{ error }}</section>
  </div>
</template>

<script>
import { fetchBalance } from "../services/api";

export default {
  name: "OverviewView",
  data() {
    return {
      balance: null,
      loading: false,
      error: "",
      palette: ["#38bdf8", "#f59e0b", "#22c55e", "#e879f9", "#60a5fa", "#fb7185"],
      selectedType: "ALL",
      donutTooltip: {
        visible: false,
        x: 0,
        y: 0,
        label: "",
        pct: 0,
        type: "group",
      },
    };
  },
  computed: {
    summary() {
      if (!Array.isArray(this.balance) || this.balance.length === 0) {
        return null;
      }

      const assets = this.balance.map((item) => {
        const trades = Array.isArray(item.trades) ? item.trades : [];
        const buyTotal = trades
          .filter((trade) => trade.type === "BUY")
          .reduce((sum, trade) => sum + Number(trade.amount_eur || 0), 0);
        const sellTotal = trades
          .filter((trade) => trade.type === "SELL")
          .reduce((sum, trade) => sum + Number(trade.amount_eur || 0), 0);
        const fees = trades.reduce((sum, trade) => sum + Number(trade.amount_fee || 0), 0);
        let netPaid = buyTotal - sellTotal;
        const currentValue =
          item.balance_eur == null ? null : Number(item.balance_eur || 0);
        if (!trades.length && currentValue != null) {
          netPaid = currentValue;
        }
        const profitLossPct =
          netPaid > 0 && currentValue != null
            ? ((currentValue - netPaid) / netPaid) * 100
            : null;

        const platform =
          item.platform ||
          (item.source && String(item.source).toUpperCase() === "EXTERNAL"
            ? "Externo"
            : "Bitpanda");

        return {
          asset: item.symbol,
          group: item.type,
          totalPaidEur: netPaid,
          totalFeesEur: fees,
          currentValueEur: currentValue,
          profitLossPct,
          currentPrice: item.current_price,
          amount: item.amount,
          source: item.source,
          platform,
        };
      });

      const totals = assets.reduce(
        (acc, asset) => {
          acc.totalPaidEur += Number(asset.totalPaidEur || 0);
          acc.totalFeesEur += Number(asset.totalFeesEur || 0);
          acc.currentValueEur += Number(asset.currentValueEur || 0);
          return acc;
        },
        { totalPaidEur: 0, totalFeesEur: 0, currentValueEur: 0 }
      );

      totals.profitLossPct =
        totals.totalPaidEur > 0
          ? ((totals.currentValueEur - totals.totalPaidEur) / totals.totalPaidEur) *
            100
          : null;

      const groupMap = assets.reduce((acc, asset) => {
        const key = asset.group || "OTHER";
        if (!acc[key]) {
          acc[key] = {
            group: key,
            totalPaidEur: 0,
            totalFeesEur: 0,
            currentValueEur: 0,
          };
        }
        acc[key].totalPaidEur += Number(asset.totalPaidEur || 0);
        acc[key].totalFeesEur += Number(asset.totalFeesEur || 0);
        acc[key].currentValueEur += Number(asset.currentValueEur || 0);
        return acc;
      }, {});

      const groups = Object.values(groupMap).map((group) => ({
        ...group,
        profitLossPct:
          group.totalPaidEur > 0
            ? ((group.currentValueEur - group.totalPaidEur) / group.totalPaidEur) *
              100
            : null,
      }));

      groups.sort((a, b) => {
        const diff = Number(b.currentValueEur || 0) - Number(a.currentValueEur || 0);
        if (diff !== 0) return diff;
        return String(a.group || "").localeCompare(String(b.group || ""));
      });
      assets.sort((a, b) => Number(b.currentValueEur || 0) - Number(a.currentValueEur || 0));

      const groupTotalValue = groups.reduce(
        (sum, group) => sum + Number(group.currentValueEur || 0),
        0
      );
      groups.forEach((group) => {
        group.sharePct =
          groupTotalValue > 0
            ? (Number(group.currentValueEur || 0) / groupTotalValue) * 100
            : null;
      });

      const missingPriceAssets = assets
        .filter((asset) => asset.currentPrice == null || asset.currentValueEur == null)
        .map((asset) => asset.asset);

      return {
        totals,
        groups,
        assets,
        missingPriceAssets,
      };
    },
    donutStyle() {
      if (!this.viewSummary?.groups?.length) {
        return { background: "conic-gradient(#334155 0 100%)" };
      }
      const total = this.viewSummary.groups.reduce(
        (sum, group) => sum + Number(group.currentValueEur || 0),
        0
      );
      if (!total) {
        return { background: "conic-gradient(#334155 0 100%)" };
      }
      let cursor = 0;
      const segments = this.viewSummary.groups.map((group, index) => {
        const value = Number(group.currentValueEur || 0);
        const pct = (value / total) * 100;
        const start = cursor;
        const end = cursor + pct;
        cursor = end;
        return `${this.palette[index % this.palette.length]} ${start}% ${end}%`;
      });
      return { background: `conic-gradient(${segments.join(", ")})` };
    },
    donutSegments() {
      if (!this.viewSummary?.groups?.length) return [];
      const total = this.viewSummary.groups.reduce(
        (sum, group) => sum + Number(group.sharePct || 0),
        0
      );
      if (!total) return [];
      const radius = 42;
      const circumference = 2 * Math.PI * radius;
      let cumulative = 0;
      return this.viewSummary.groups.map((group, index) => {
        const pct = Number(group.sharePct || 0) / 100;
        const segment = {
          key: `group-${group.group || index}`,
          label: this.groupLabel(group.group),
          pct: pct * 100,
          color: this.palette[index % this.palette.length],
          dashArray: `${pct * circumference} ${circumference}`,
          dashOffset: -circumference * cumulative,
        };
        cumulative += pct;
        return segment;
      });
    },
    platformGroups() {
      if (!this.viewSummary?.assets?.length) return [];
      const map = this.viewSummary.assets.reduce((acc, asset) => {
        const key = asset.platform || "Sin plataforma";
        if (!acc[key]) {
          acc[key] = {
            platform: key,
            totalPaidEur: 0,
            currentValueEur: 0,
          };
        }
        acc[key].totalPaidEur += Number(asset.totalPaidEur || 0);
        acc[key].currentValueEur += Number(asset.currentValueEur || 0);
        return acc;
      }, {});
      const groups = Object.values(map).map((group) => ({
        ...group,
        profitLossPct:
          group.totalPaidEur > 0
            ? ((group.currentValueEur - group.totalPaidEur) / group.totalPaidEur) * 100
            : null,
      }));
      groups.sort((a, b) => {
        const diff = Number(b.currentValueEur || 0) - Number(a.currentValueEur || 0);
        if (diff !== 0) return diff;
        return String(a.platform || "").localeCompare(String(b.platform || ""));
      });
      const totalValue = groups.reduce(
        (sum, group) => sum + Number(group.currentValueEur || 0),
        0
      );
      groups.forEach((group) => {
        group.sharePct =
          totalValue > 0 ? (Number(group.currentValueEur || 0) / totalValue) * 100 : null;
      });
      return groups;
    },
    platformDonutStyle() {
      if (!this.platformGroups.length) {
        return { background: "conic-gradient(#334155 0 100%)" };
      }
      const total = this.platformGroups.reduce(
        (sum, group) => sum + Number(group.currentValueEur || 0),
        0
      );
      if (!total) {
        return { background: "conic-gradient(#334155 0 100%)" };
      }
      let cursor = 0;
      const segments = this.platformGroups.map((group, index) => {
        const value = Number(group.currentValueEur || 0);
        const pct = (value / total) * 100;
        const start = cursor;
        const end = cursor + pct;
        cursor = end;
        return `${this.palette[index % this.palette.length]} ${start}% ${end}%`;
      });
      return { background: `conic-gradient(${segments.join(", ")})` };
    },
    platformSegments() {
      if (!this.platformGroups.length) return [];
      const total = this.platformGroups.reduce(
        (sum, group) => sum + Number(group.sharePct || 0),
        0
      );
      if (!total) return [];
      const radius = 42;
      const circumference = 2 * Math.PI * radius;
      let cumulative = 0;
      return this.platformGroups.map((platform, index) => {
        const pct = Number(platform.sharePct || 0) / 100;
        const segment = {
          key: `platform-${platform.platform || index}`,
          label: this.platformLabel(platform.platform),
          pct: pct * 100,
          color: this.palette[index % this.palette.length],
          dashArray: `${pct * circumference} ${circumference}`,
          dashOffset: -circumference * cumulative,
        };
        cumulative += pct;
        return segment;
      });
    },
    viewSummary() {
      if (!this.summary) return null;
      if (this.selectedType === "ALL") return this.summary;
      const assets = this.filteredAssets;
      const totals = assets.reduce(
        (acc, asset) => {
          acc.totalPaidEur += Number(asset.totalPaidEur || 0);
          acc.totalFeesEur += Number(asset.totalFeesEur || 0);
          acc.currentValueEur += Number(asset.currentValueEur || 0);
          return acc;
        },
        { totalPaidEur: 0, totalFeesEur: 0, currentValueEur: 0 }
      );
      totals.profitLossPct =
        totals.totalPaidEur > 0
          ? ((totals.currentValueEur - totals.totalPaidEur) / totals.totalPaidEur) * 100
          : null;

      const groupMap = assets.reduce((acc, asset) => {
        const key = asset.group || "OTHER";
        if (!acc[key]) {
          acc[key] = {
            group: key,
            totalPaidEur: 0,
            totalFeesEur: 0,
            currentValueEur: 0,
          };
        }
        acc[key].totalPaidEur += Number(asset.totalPaidEur || 0);
        acc[key].totalFeesEur += Number(asset.totalFeesEur || 0);
        acc[key].currentValueEur += Number(asset.currentValueEur || 0);
        return acc;
      }, {});

      const groups = Object.values(groupMap).map((group) => ({
        ...group,
        profitLossPct:
          group.totalPaidEur > 0
            ? ((group.currentValueEur - group.totalPaidEur) / group.totalPaidEur) * 100
            : null,
      }));

      groups.sort((a, b) => {
        const diff = Number(b.currentValueEur || 0) - Number(a.currentValueEur || 0);
        if (diff !== 0) return diff;
        return String(a.group || "").localeCompare(String(b.group || ""));
      });
      assets.sort((a, b) => Number(b.currentValueEur || 0) - Number(a.currentValueEur || 0));

      const groupTotalValue = groups.reduce(
        (sum, group) => sum + Number(group.currentValueEur || 0),
        0
      );
      groups.forEach((group) => {
        group.sharePct =
          groupTotalValue > 0
            ? (Number(group.currentValueEur || 0) / groupTotalValue) * 100
            : null;
      });

      const missingPriceAssets = assets
        .filter((asset) => asset.currentPrice == null || asset.currentValueEur == null)
        .map((asset) => asset.asset);

      return { totals, groups, assets, missingPriceAssets };
    },
    availableTypes() {
      if (!this.summary?.assets?.length) return [];
      const types = new Set(this.summary.assets.map((asset) => String(asset.group || "OTHER")));
      return Array.from(types);
    },
    filteredAssets() {
      if (!this.summary?.assets?.length) return [];
      if (this.selectedType === "ALL") return this.summary.assets;
      return this.summary.assets.filter((asset) => asset.group === this.selectedType);
    },
    missingPriceAssetsFiltered() {
      return this.filteredAssets
        .filter((asset) => asset.currentPrice == null || asset.currentValueEur == null)
        .map((asset) => asset.asset);
    },
    topAssets() {
      if (!this.filteredAssets.length) return [];
      return this.filteredAssets.slice(0, 6);
    },
    topMax() {
      if (!this.topAssets.length) return 0;
      return Math.max(...this.topAssets.map((asset) => Number(asset.currentValueEur || 0)), 0);
    },
    summaryTitle() {
      if (this.selectedType === "ALL") return "Resumen total";
      return `Resumen · ${this.groupLabel(this.selectedType)}`;
    },
  },
    async mounted() {
      this.loading = true;
      this.error = "";
      try {
        this.balance = await fetchBalance();
      } catch (err) {
        this.error = err?.message || "No se pudo cargar el balance.";
      } finally {
        this.loading = false;
      }
  },
  methods: {
    formatEur(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(Number(value));
    },
    formatPct(value) {
      if (value == null) return "—";
      const number = Number(value);
      const sign = number > 0 ? "+" : "";
      return `${sign}${number.toFixed(2)}%`;
    },
    formatShare(value) {
      if (value == null) return "—";
      return `${Number(value).toFixed(2)}%`;
    },
    onDonutMove(event, segment, type) {
      const svg = event.currentTarget?.ownerSVGElement;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      this.donutTooltip = {
        visible: true,
        x,
        y,
        label: segment.label,
        pct: segment.pct,
        type,
      };
    },
    hideDonutTooltip(type) {
      if (this.donutTooltip.type === type) {
        this.donutTooltip.visible = false;
      }
    },
    barWidth(value, max) {
      if (!max || value == null) return "0%";
      return `${Math.min((Number(value) / max) * 100, 100)}%`;
    },
    profitClass(value) {
      if (value == null) return "muted";
      return Number(value) >= 0 ? "positive" : "negative";
    },
    valueClass(totals) {
      if (!totals) return "muted";
      const paid = Number(totals.totalPaidEur || 0);
      const current = Number(totals.currentValueEur || 0);
      return current >= paid ? "positive" : "negative";
    },
    groupLabel(group) {
      const map = {
        CRYPTO: "Cripto",
        METAL: "Metales",
        FIAT: "Fiat",
        STOCK: "Acciones",
        ETF: "ETFs",
        INDEX: "Índices",
        CASH: "Dinero",
        MICROLENDING: "Micro‑lending",
        BOND: "Bonos",
        OTHER: "Otros",
      };
      const key = group ? String(group).toUpperCase() : "OTHER";
      return map[key] || group;
    },
    platformLabel(value) {
      if (!value) return "Sin plataforma";
      const raw = String(value).trim();
      const cleaned = raw.replace(/_/g, " ");
      if (cleaned === cleaned.toUpperCase()) {
        return cleaned
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
      }
      return cleaned;
    },
    goToAsset(symbol) {
      const asset = this.summary?.assets?.find((item) => item.asset === symbol);
      if (asset?.source === "EXTERNAL") {
        this.$router.push("/dashboard/external-assets");
        return;
      }
      this.$router.push(`/dashboard/assets/${symbol}`);
    },
  },
};
</script>

<style scoped>
.overview {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
}

.card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow);
}

.card.highlight {
  background: linear-gradient(160deg, rgba(56, 189, 248, 0.16), rgba(15, 23, 42, 0.9));
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
}

.chip {
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.4);
  color: var(--accent);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.total-hero {
  grid-column: 1 / -1;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(56, 189, 248, 0.2);
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.total-hero p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  letter-spacing: 0.02em;
}

.total-hero h3 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.totals p {
  margin: 0 0 6px;
  color: var(--muted);
  font-size: 13px;
}

.totals h3 {
  margin: 0;
  font-size: 20px;
}

.chart {
  min-height: 300px;
}

.donut-wrap {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
}

.donut {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  position: relative;
  box-shadow: inset 0 0 0 12px rgba(15, 23, 42, 0.8);
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.donut-ring {
  fill: transparent;
  stroke: rgba(148, 163, 184, 0.2);
  stroke-width: 14;
}

.donut-segment {
  fill: transparent;
  stroke-width: 14;
  stroke-linecap: butt;
  transition: opacity 0.2s ease;
}

.donut-segment:hover {
  opacity: 0.85;
}

.donut-tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 6px 8px;
  font-size: 12px;
  color: var(--text);
  transform: translate(-50%, -120%);
  white-space: nowrap;
  box-shadow: var(--shadow);
}

.donut-tooltip strong {
  display: block;
  font-size: 12px;
}

.donut-tooltip span {
  color: var(--muted);
  font-size: 11px;
}

.donut-legend {
  display: grid;
  gap: 12px;
}

.legend-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  background: var(--panel-2);
  border-radius: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
}

.legend-item p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.legend-pct {
  font-weight: 600;
  color: var(--text);
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.table-card {
  overflow: hidden;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.filter-chip {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: var(--muted);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-chip.active {
  background: rgba(56, 189, 248, 0.2);
  border-color: rgba(56, 189, 248, 0.5);
  color: var(--text);
}

.filter-chip:hover {
  background: rgba(56, 189, 248, 0.12);
}

.bars {
  display: grid;
  gap: 12px;
}

.bar-row {
  display: grid;
  grid-template-columns: 140px 1fr 120px;
  align-items: center;
  gap: 16px;
}

.bar-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bar-track {
  background: rgba(148, 163, 184, 0.16);
  border-radius: 999px;
  height: 10px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(56, 189, 248, 0.9), rgba(34, 197, 94, 0.9));
}

.bar-value {
  text-align: right;
  font-weight: 600;
}

.table {
  display: grid;
}

.row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 14px 12px;
  align-items: center;
  border-top: 1px solid var(--border);
  background: transparent;
  color: var(--text);
  border: none;
  text-align: left;
  cursor: pointer;
}

.row.head {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--muted);
  border-top: none;
  cursor: default;
}

.row:hover {
  background: rgba(56, 189, 248, 0.08);
}

.asset-name {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tag {
  background: rgba(56, 189, 248, 0.2);
  color: var(--accent);
  padding: 4px 8px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 12px;
  width: fit-content;
}

.muted {
  color: var(--muted);
}

.positive {
  color: var(--accent-3);
}

.negative {
  color: var(--danger);
}

.notice {
  margin-top: 14px;
  font-size: 13px;
  color: var(--muted);
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.skeleton-summary .card {
  display: grid;
  gap: 18px;
}

.skeleton-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.skeleton-chart {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 20px;
  align-items: center;
}

.skeleton-bars {
  display: grid;
  gap: 16px;
}

.skeleton-table-rows {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

@media (max-width: 900px) {
  .donut-wrap {
    grid-template-columns: 1fr;
  }

  .row {
    grid-template-columns: 1.2fr 1fr 1fr;
  }

  .row span:nth-child(4),
  .row span:nth-child(5) {
    display: none;
  }

  .bar-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .bar-value {
    text-align: left;
  }
}
</style>
